// src/components/settings/team-leads-tab.jsx
// Owner-only control over WHO can be a team lead — turn each team lead on/off
// (bookable on new files or not). Lives under Super Admin. Turning one off only
// removes them from the team-lead picker; their past deals and stats stay.
import { useState } from "react";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { getAllusers, updateUserProfile } from "../../utils/firebasefunctions";
import { toast } from "react-toastify";

export default function TeamLeadsTab() {
  const [{ users, user }, dispatch] = useStateValue();
  const [busyId, setBusyId] = useState(null);

  const teamLeads = (users || []).filter(
    (u) => u.role === "Admin" && !u.isDemo && u.type !== 1,
  );

  const refresh = async () => {
    const response = await getAllusers("users");
    dispatch({
      type: actionType.STORE_ALLUSERS,
      teamleads: response.filter((d) => d.role === "Admin"),
      clients: response.filter((d) => d.role === "Supervisor"),
      employees: response,
      allusers: response,
    });
  };

  const toggle = async (item) => {
    const next = item.activeteamlead === false ? true : false;
    setBusyId(item.id);
    try {
      await updateUserProfile(item.id, { activeteamlead: next });
      await refresh();
      toast.success(next ? "Team lead turned on" : "Team lead turned off");
    } catch (err) {
      toast.error("Could not update team lead status");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-2xl">
      <h3 className="text-lg font-semibold text-slate-900">Team Leads</h3>
      <p className="text-sm text-slate-500 mt-1 mb-4">
        Who can be picked as a team lead when a file is turned in. Turn one off to
        remove them from the team-lead dropdown — their past deals and stats stay.
      </p>
      <div className="space-y-2">
        {teamLeads.length === 0 && (
          <p className="text-sm text-slate-400">No team leads yet.</p>
        )}
        {teamLeads.map((item) => {
          const off = item.activeteamlead === false;
          const isSelf = item.id === user.id;
          const initials =
            `${(item.firstname || "")[0] || ""}${
              (item.lastname || "")[0] || ""
            }`.toUpperCase() || "?";
          return (
            <div
              key={item.id}
              className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-3 bg-white"
            >
              <div className="size-9 rounded-full bg-slate-100 text-linkColor flex items-center justify-center text-[13px] font-medium flex-shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {item.firstname} {item.lastname}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {item.email}
                </div>
              </div>
              {/* Only the exception gets a badge. A green "Bookable" pill sat
                  on nearly every row, which made it decoration rather than
                  information — and the toggle beside it already says the same
                  thing. "Off" is the state actually worth spotting. */}
              {off && (
                <span className="text-xs px-2.5 py-1 rounded-full flex-shrink-0 bg-gray-100 text-gray-500">
                  Off
                </span>
              )}
              <button
                type="button"
                disabled={busyId === item.id || isSelf}
                onClick={() => toggle(item)}
                title={
                  isSelf
                    ? "You can't turn yourself off"
                    : off
                      ? "Turn on as team lead"
                      : "Turn off as team lead"
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 disabled:opacity-40 ${
                  off ? "bg-gray-300" : "bg-green-500"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    off ? "translate-x-1" : "translate-x-5"
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
